import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Heading,
    LoadingPanel,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient } from '../../state';
import { encodeDocumentForAPI, useBasicEditorValue } from '../BasicEditor';
import { TranslationInstructionInput } from './TranslationInstructionInput';

interface EditTranslationDialogProps {
    organizationId: string;
    translation: api.Translation;
}

/**
 * Dialog to edit a translation.
 */
export function EditTranslationDialog(props: EditTranslationDialogProps) {
    return (
        <Dialog>
            <EditTranslationDialogContent {...props} />
        </Dialog>
    );
}

function EditTranslationDialogContent(props: EditTranslationDialogProps) {
    const { organizationId, translation } = props;

    useSetAnalyticsScreen({
        screen: 'translation_edit',
    });

    return (
        <>
            <DialogHeader className="w-screen max-w-xl">
                <Heading>Edit translation</Heading>
                <Description>Edit the advanced configuration of the translation.</Description>
            </DialogHeader>
            <React.Suspense fallback={<LoadingPanel size="large" />}>
                <EditTranslationForm organizationId={organizationId} translation={translation} />
            </React.Suspense>
        </>
    );
}

/**
 * Form wrapped in the dialog to edit a translation.
 */
function EditTranslationForm(props: { organizationId: string; translation: api.Translation }) {
    const { organizationId, translation } = props;

    const closeDialog = useDialogClose();
    const onSubmit = async () => {
        await toast.catch(async () => {
            await apiClient.translations.updateTranslation(organizationId, translation.id, {
                instructions: encodeDocumentForAPI(instructions.value.document),
            });
            analytics.logEvent({
                type: 'translation_edit',
                organization: organizationId,
                sourceLanguage: translation.source.language,
                targetLanguage: translation.language,
            });
        }, 'Error while updating translation');

        toast.success('Translation updated');
        closeDialog();
    };

    const instructions = useBasicEditorValue(translation.instructions);

    return (
        <>
            <DialogBody>
                <TranslationInstructionInput
                    value={instructions.value}
                    onChange={instructions.setValue}
                />
            </DialogBody>
            <DialogFooter>
                <Button kind="secondary" label="Cancel" onClick={closeDialog} />
                <Button label="Save" onPress={onSubmit} />
            </DialogFooter>
        </>
    );
}
